import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

const VIEWPORT_MARGIN = 4;

/**
 * Repositions a fixed-position menu so it stays within the viewport, flipping it to the
 * other side of the click point when it would otherwise overflow. Runs in useLayoutEffect
 * (not useEffect) so the correction lands before the browser paints — no visible flash at
 * the original off-screen position.
 *
 * ResizeObserver re-runs the same clamp whenever the menu's own size changes after that first
 * measurement — needed for menus with async-loaded content (e.g. BranchContextMenu's pipeline/
 * Jira sections): without it, a click near the right/bottom edge is clamped against the size at
 * first paint (often just a loading state), and the menu can end up rendered off-screen once the
 * real content grows the box. Harmless no-op for menus with static content (CommitContextMenu,
 * TagContextMenu) — their size never changes, so the observer just never fires again.
 *
 * `freeze` opts out of that re-clamping without tearing down the observer (BranchContextMenu
 * passes `linking`) — once the user has opened the Jira issue picker, every keystroke resizes
 * `.jira-tooltip-editor-list` as results come and go, and re-clamping on each of those would
 * relocate the whole menu (possibly flipping above/below the click point) right out from under
 * the cursor, breaking focus/hover on the search input mid-search. By the time that picker is
 * open, the async pipeline/Jira sections above it have already settled, so there is nothing left
 * worth re-clamping for — freezing just stops a resize that no longer needs a reaction. A ref
 * (not a dependency) so toggling it doesn't recreate the observer and drop the last good position.
 */
export function useClampedMenuPosition(
  ref: RefObject<HTMLElement | null>,
  x: number,
  y: number,
  freeze = false,
): { left: number; top: number } {
  const [pos, setPos] = useState({ left: x, top: y });
  const freezeRef = useRef(freeze);
  freezeRef.current = freeze;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reposition = () => {
      if (freezeRef.current) return;
      const rect = el.getBoundingClientRect();
      let left = x;
      let top = y;
      if (left + rect.width > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, x - rect.width);
      }
      if (top + rect.height > window.innerHeight - VIEWPORT_MARGIN) {
        top = Math.max(VIEWPORT_MARGIN, y - rect.height);
      }
      setPos({ left, top });
    };
    reposition();
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, x, y]);

  return pos;
}
